import { Badge, Box } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import {
  BetaIcon,
  SuperAdminIcon,
} from 'components/common/custom-icon/shorthands';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import env from 'config';
import { MachineContext } from 'contexts/machine.context';
import {
  isTrackingDeviceBeta,
  isTrackingDeviceEnabled,
  isTrackingDeviceVisible,
} from 'enums/beta';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { RegexFilter } from 'interfaces/forms/text';
import {
  MachineHelper,
  getDistanceOptions,
} from 'lib_ts/classes/machine.helper';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { VisionHelper } from 'lib_ts/classes/vision.helper';
import { BallType, StrikeZoneType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  FF_NAME_ON_DEMAND_MODE,
  TrackingDevice,
} from 'lib_ts/enums/training.enums';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { isNil } from 'lodash';
import { useContext, useMemo } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

interface IProps {
  machine: Partial<IMachine>;
  conflictedMachineID: boolean;
  updateMetadata: (model: Partial<IMachine>) => void;
}

export const MachineSettingsTab = ({
  machine,
  conflictedMachineID,
  updateMetadata,
}: IProps) => {
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);

  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const { logPlateDistanceChange } = useContext(MachineContext);

  const updating = useMachinesPortalStore((s) => s.updating);

  const deviceOptions = useMemo(
    () =>
      Object.values(TrackingDevice).map((m) => {
        const o: IOptionExt = {
          label: m,
          value: m,
          invisible: !isTrackingDeviceVisible(m, machine),
          disabled: !isTrackingDeviceEnabled(m, machine),
          suffix: isTrackingDeviceBeta(m) ? <BetaIcon /> : undefined,
        };

        return o;
      }),
    [machine]
  );

  const trainingThresholdOptions = useMemo(
    () =>
      TrainingHelper.getTrainingThresholdOptions(
        machine.training_threshold ?? null
      ),
    [machine.training_threshold]
  );

  const insetFt = useMemo(
    () =>
      machine.hardware_version
        ? MachineHelper.getWheelsInsetFt(machine.hardware_version)
        : 0,
    [machine.hardware_version]
  );

  const plateDistanceFt = useMemo(() => {
    if (isNil(machine.releaseFt)) {
      return undefined;
    }

    const output = Math.max(0, machine.releaseFt - insetFt);
    return output;
  }, [machine.releaseFt, insetFt]);

  const plateDistanceOption = useMemo(() => {
    if (isNil(plateDistanceFt)) {
      return undefined;
    }

    return MachineHelper.getDistanceOptionValue(plateDistanceFt);
  }, [plateDistanceFt]);

  const releaseDistance = useMemo(
    () => MachineHelper.getDistanceOptionValue(machine.releaseFt)?.value,
    [machine.releaseFt]
  );

  const plateDistanceOptions = useMemo(
    () => getDistanceOptions(plateDistanceFt),
    [plateDistanceFt]
  );

  const releaseDistanceOptions = useMemo(
    () => getDistanceOptions(machine.releaseFt),
    [machine.releaseFt]
  );

  const onVision = machine.tracking_device === TrackingDevice.TrajektVision;
  const releaseSetByVision =
    VisionHelper.ALLOW_VISION_RELEASE_DISTANCE_UPDATE && onVision;

  return (
    <CommonFormGrid columns={2}>
      <Box gridColumn="span 1">
        <CommonTextInput
          id="machine-editor-machineID"
          label="MachineID"
          disabled={!!machine._id || updating}
          placeholder="Type a unique value"
          value={machine.machineID}
          regex={RegexFilter.AlphaNumeric}
          onChange={(v) => {
            updateMetadata({
              machineID: v,
              rapsodo_serial: v,
            });
          }}
          hint_md={
            conflictedMachineID ? 'MachineID is already in use' : undefined
          }
        />
      </Box>

      <Box gridColumnStart="1">
        <CommonSelectInput
          id="machine-editor-plate-distance"
          label="Plate Distance (ft)"
          labelIcon={
            releaseSetByVision && (
              <Badge color={RADIX.COLOR.NEUTRAL}>
                {t('settings.set-by-vision')}
              </Badge>
            )
          }
          disabled={updating || releaseSetByVision}
          options={plateDistanceOptions}
          value={plateDistanceOption?.value}
          onNumericChange={(nextPlateDistanceFt) => {
            const nextReleaseFt = nextPlateDistanceFt + insetFt;

            if (nextReleaseFt === machine.releaseFt) {
              // avoid unnecessary overwriting
              return;
            }

            const prevPlateFt = isNil(machine.releaseFt)
              ? null
              : machine.releaseFt - insetFt;

            logPlateDistanceChange({
              prevPlateFt: prevPlateFt,
              nextPlateFt: nextPlateDistanceFt,
              overrideMachineID: machine.machineID,
            });

            const nextValue: Partial<IMachine> = {
              releaseFt: nextReleaseFt,
              last_plate_distance_ft: prevPlateFt ?? undefined,
              last_plate_distance_changed: new Date().toISOString(),
              last_plate_distance_changedBy: current.email,
            };

            updateMetadata(nextValue);
          }}
          skipSort
          suppressWarning
        />
      </Box>

      <CommonSelectInput
        id="machine-editor-release-distance"
        label="Release Distance (ft)"
        options={releaseDistanceOptions}
        value={releaseDistance}
        onNumericChange={(nextReleaseFt) => {
          // shouldn't trigger
          if (nextReleaseFt === machine.releaseFt) {
            // avoid unnecessary overwriting
            return;
          }

          const prevPlateFt = isNil(machine.releaseFt)
            ? null
            : machine.releaseFt - insetFt;

          logPlateDistanceChange({
            prevPlateFt: prevPlateFt,
            nextPlateFt: nextReleaseFt - insetFt,
            overrideMachineID: machine.machineID,
          });

          const nextValue: Partial<IMachine> = {
            releaseFt: nextReleaseFt,
            last_plate_distance_ft: prevPlateFt ?? undefined,
            last_plate_distance_changed: new Date().toISOString(),
            last_plate_distance_changedBy: current.email,
          };

          updateMetadata(nextValue);
        }}
        skipSort
        suppressWarning
        disabled
      />

      <CommonSelectInput
        id="machine-editor-training-threshold"
        name="training_threshold"
        label="settings.training-shots"
        options={trainingThresholdOptions}
        value={machine.training_threshold?.toString()}
        onNumericChange={(v) => {
          updateMetadata({ training_threshold: v });
        }}
        disabled={updating}
        skipSort
      />
      <CommonSelectInput
        id="machine-editor-units"
        name="isMetric"
        label="Units"
        options={[
          { label: 'Imperial', value: 'false' },
          { label: 'Metric', value: 'true' },
        ]}
        value={machine.isMetric?.toString()}
        onOptionalBooleanChange={(v) => {
          updateMetadata({ isMetric: v });
        }}
        hint_md="For changing the units displayed by the machine (e.g. pitch recap and preview speeds in MPH vs KPH)."
        disabled={updating}
        optional
      />
      <CommonSelectInput
        id="machine-editor-ball"
        name="ball_type"
        label="common.ball-type"
        options={(machine.ball_type_options ?? [BallType.MLB]).map((t) => ({
          label: t,
          value: t,
        }))}
        value={machine.ball_type}
        onChange={(v) =>
          updateMetadata({
            ball_type: v as BallType,
            last_ball_type_changed: new Date().toISOString(),
          })
        }
        disabled={updating}
      />

      {!env.production && (
        <CommonSelectInput
          id="machine-editor-tracking-device"
          label="Tracking Device"
          options={deviceOptions}
          value={machine.tracking_device}
          onChange={(v) => {
            if (v !== TrackingDevice.TrajektVision && onDemand) {
              // do nothing
              NotifyHelper.warning({
                message_md:
                  'Please disable On-Demand before switching away from Trajekt Vision.',
              });
              return;
            }

            const safeValue = v as TrackingDevice;
            updateMetadata({ tracking_device: safeValue });
          }}
          disabled={!!machine._id || updating}
        />
      )}

      <Box gridColumnStart="1">
        <CommonTextInput
          id="machine-editor-rapsodo-serial"
          label="Rapsodo Serial"
          placeholder="Type a unique value"
          value={machine.rapsodo_serial}
          disabled={machine.sandbox || updating}
          onChange={(v) =>
            updateMetadata({
              rapsodo_serial: (v ?? '').trim(),
            })
          }
          hint_md={
            machine.sandbox
              ? "A sandbox's Rapsodo Serial must match its MachineID."
              : "Marked as `SN` on the unit's sticker."
          }
          monospace
        />
      </Box>

      {userRoleFeatureFlags.adminMachines.changeStrikeZoneType && (
        <CommonSelectInput
          id="machine-editor-strike-zone-type"
          name="strike-zone-type"
          label="common.strike-zone-type"
          labelIcon={<SuperAdminIcon />}
          options={Object.values(StrikeZoneType).map((t) => ({
            label: t,
            value: t,
          }))}
          value={machine.strikeZoneType}
          onChange={(v) =>
            updateMetadata({
              strikeZoneType: v as StrikeZoneType,
            })
          }
          disabled={updating}
        />
      )}
    </CommonFormGrid>
  );
};
